import { DOMAIN } from '../../utils/api'
import { mainGreen } from '../../utils/color'

export const adminRoles = ['admin', 'director', 'assistant']
export const SHIPPING_EXPENSE_NAME = 'Chi phí ship'
export const DEFAULT_SHIPPING_EXPENSE_CATEGORY_NAME = SHIPPING_EXPENSE_NAME
export const DEFAULT_OTHER_EXPENSE_CATEGORY_NAME = 'Chi khác'
export const DEFAULT_WITHDRAW_EXPENSE_CATEGORY_NAME = 'Rút tiền'
export const DEFAULT_CASH_SOURCE = 'cash_default'
// Khoản chi mặc định do hệ thống tạo, không sửa/xoá trong danh mục
export const RESERVED_EXPENSE_CATEGORY_TYPES = ['shipping', 'cashout']
export const EXPENSE_SOURCES = ['cash', 'bank']

export const todayInputValue = () => {
	const now = new Date()
	const offset = now.getTimezoneOffset()
	const local = new Date(now.getTime() - offset * 60 * 1000)
	return local.toISOString().slice(0, 10)
}

// Số liệu đang giữ trong state có thể là của ngày trước trong lúc chờ tải ngày mới,
// nên chỉ coi là đúng ngày khi báo cáo trả về khớp với ngày đang chọn
export const summaryMatchesDate = (summary, date) =>
	Boolean(summary) && (!summary.reportDate || summary.reportDate === date)

export const formatMoney = (value) => `${(Number(value) || 0).toLocaleString()}đ`

export const parseMoney = (value) => {
	if (typeof value === 'number') return value
	return Number(String(value || '').replace(/[^\d.-]/g, '')) || 0
}

export const hasValue = (value) => value !== '' && value !== undefined && value !== null

export const orderCode = (order) => {
	if (!order) return ''
	const shiftCode = `${order.shiftCode || ''}${order.shiftIndex || ''}`
	return shiftCode || order.uniqueCode || ''
}

export const orderContactText = (order) =>
	[order?.customerName, order?.customerPhone, order?.customerAddress].filter(Boolean).join(' - ')

export const orderItemsText = (order) => {
	const items = (order?.line_items || []).filter((item) => Number(item.quantity) > 0)
	if (items.length === 0) return ''
	const visibleItems = items.slice(0, 3).map((item) => `${item.quantity}x ${item.name}`)
	return items.length > 3 ? `${visibleItems.join(', ')} +${items.length - 3}` : visibleItems.join(', ')
}

export const toId = (value) => value?._id || value || ''

export const isTransferredOrder = (order) => Boolean(toId(order?.transferStore?.fromStore))

export const isTransferredBankOrder = (order) => isTransferredOrder(order) && order?.payment === 'transfer'

export const accountTypeLabel = (accountOrType) => {
	const accountType = typeof accountOrType === 'string' ? accountOrType : accountOrType?.accountType || 'bank'
	if (accountType === 'cash') return 'Tiền mặt'
	if (accountType === 'app') return 'App'
	return 'Ngân hàng'
}

export const paymentLabel = (paymentOrOrder) => {
	if (typeof paymentOrOrder === 'object') {
		if (paymentOrOrder?.app) return 'App'
		return paymentOrOrder?.payment === 'transfer' ? 'Chuyển khoản' : 'Tiền mặt'
	}
	return paymentOrOrder === 'transfer' ? 'Chuyển khoản' : 'Tiền mặt'
}

export const accountTypeForPayment = (payment) => (payment === 'transfer' ? 'bank' : 'cash')

export const accountTypeForOrder = (order) => (order?.app ? 'app' : accountTypeForPayment(order?.payment))

export const orderUsesDefaultCashflowChannel = (order) => ['cash', 'app'].includes(accountTypeForOrder(order))

export const orderNeedsManualBankAccount = (order) => accountTypeForOrder(order) === 'bank'

export const expenseTypeLabel = (type) => {
	if (type === 'shipping') return 'Chi ship'
	if (type === 'cashout') return 'Rút tiền'
	return 'Chi khác'
}

export const isReservedExpenseCategory = (category) => RESERVED_EXPENSE_CATEGORY_TYPES.includes(category?.type)

export const categoryAllowsOrders = (category) => category?.allowOrderSelection !== false

export const categoryAllowsDuplicateOrders = (category) => category?.allowDuplicateOrders !== false

export const categoryRequiresApproval = (category) => category?.requiresApproval === true

// Khoản chi cấu hình được chi bằng tiền mặt, tài khoản, hoặc cả hai
export const categoryAllowedSources = (category) => {
	const sources = EXPENSE_SOURCES.filter((source) => category?.allowedSources?.includes(source))
	return sources.length > 0 ? sources : EXPENSE_SOURCES
}

export const categoryAllowsSource = (category, source) => categoryAllowedSources(category).includes(source)

export const expenseSourceLabel = (source) => (source === 'cash' ? 'Tiền mặt' : 'Tài khoản')

export const expenseNeedsApproval = (expense) => expense?.approvalRequired === true

export const expenseIsPendingApproval = (expense) =>
	expenseNeedsApproval(expense) && expense?.approvalStatus !== 'confirmed'

const CATEGORY_GROUP_COLORS = {
	shipping: { background: '#E8F5E9', color: mainGreen },
	cashout: { background: '#FDE7EF', color: '#9C1C4A' },
	other: { background: '#FFF8E1', color: '#B76E00' },
}

export const categoryGroupSx = (type) => ({
	...(CATEGORY_GROUP_COLORS[type] || CATEGORY_GROUP_COLORS.other),
	fontWeight: 800,
	lineHeight: '34px',
})

export const orderAssignmentRowSx = (order, needsAccount, index) => {
	if (needsAccount) {
		return {
			background: '#FFF8E7',
			'&:hover': { background: '#FFF1CC' },
		}
	}
	const isTransfer = order.payment === 'transfer'
	return {
		background: isTransfer ? (index % 2 === 0 ? '#F2F8FF' : '#FAFDFF') : index % 2 === 0 ? '#F1FAF4' : '#FBFDFB',
		'&:hover': { background: isTransfer ? '#E7F1FF' : '#E7F6EC' },
	}
}

export const resolveImageUrl = (url) => {
	if (!url) return ''
	if (/^(blob:|data:)/.test(url)) return url
	if (/^https?:\/\//.test(url)) {
		try {
			const parsedUrl = new URL(url)
			const isLocalPage = ['localhost', '127.0.0.1'].includes(window.location.hostname)
			if (isLocalPage && parsedUrl.pathname.startsWith('/images/')) {
				return `${DOMAIN.replace(/\/$/, '')}${parsedUrl.pathname}`
			}
		} catch (_error) {
			return url
		}
		return url
	}
	return `${DOMAIN.replace(/\/$/, '')}/${String(url).replace(/^\//, '')}`
}

export const sectionSx = {
	p: { xs: 1.5, md: 2.25 },
	border: '1px solid #DDE7DF',
	borderRadius: '14px',
	background: '#fff',
	boxShadow: '0 12px 34px rgba(24, 64, 39, 0.06)',
	width: '100%',
}
